import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { DownloadIcon } from "lucide-react";
import { useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTitle } from "react-use";

import { trpc } from "@blis/trpc/react";

import { LoadingPage } from "@/components/LoadingPage";
import { SubHeader } from "@/components/SubHeader";
import { Badge } from "@/components/ui/badge";
import { Breadcrumb, Breadcrumbs } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import { format } from "@/lib/utils";
import {
  useInitializeNavStack,
  useNavLocationState,
} from "@/utils/useNavStack";
import {
  RequisitionProvider,
  useRequisition,
} from "../components/RequisitionContext";
import { RequisitionPreview } from "../components/RequisitionPreview";

export function RequisitionPreviewPage() {
  const params = useParams({ from: "/_auth/_app/accessions_/$id/requisition" });
  const search = useSearch({ from: "/_auth/_app/accessions_/$id/requisition" });
  const id = params.id;
  const labId = (search as { labId?: number })?.labId || 0;

  useTitle(`Requisition - ${id}`);
  useInitializeNavStack();

  const { data: accession, isLoading } = trpc.accession.get.useQuery({
    id: Number(id),
  });

  const { data: samples, isLoading: samplesLoading } =
    trpc.accession.samples.useQuery(Number(id));

  const { href, label } = useNavLocationState();

  if (isLoading || samplesLoading) {
    return <LoadingPage />;
  }

  if (!accession) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        Accession not found
      </div>
    );
  }

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb href={href}>{label}</Breadcrumb>
      <Breadcrumb
        href={{ to: "/accessions/$id", params: { id }, state: { href, label } }}
      >
        {id}
      </Breadcrumb>
      <Breadcrumb>Requisition</Breadcrumb>
    </Breadcrumbs>
  );

  return (
    <RequisitionProvider
      accession={accession}
      samples={samples || []}
      labId={labId}
      setLabId={() => {}} // We'll implement this if needed
    >
      <SubHeader>
        <SubHeader.Left>{breadcrumbs}</SubHeader.Left>
        {/* <SubHeader.Right>
          <DownloadButton />
        </SubHeader.Right> */}
      </SubHeader>
      <div className="flex-1 flex flex-col lg:flex-row">
        <div className="flex flex-col items-center flex-1 w-full">
          <div className="flex flex-col items-center flex-1 p-5 w-full">
            <RequisitionPreview />
          </div>
        </div>

        <SampleSelectionSidebar />
      </div>
    </RequisitionProvider>
  );
}

function SampleSelectionSidebar() {
  return (
    <div className="p-5 w-full lg:max-w-[350px] border-l">
      <SampleSelectionForm />
    </div>
  );
}

type FormValues = {
  selectedSamples: number[];
};

function SampleSelectionForm() {
  const { samples, selectedSamples, setSelectedSamples } = useRequisition();

  const onSave = useCallback(
    ({ selectedSamples }: FormValues) => {
      if (selectedSamples.length === 0) {
        // Don't allow empty selection - at least one sample should be selected
        return;
      }
      setSelectedSamples(selectedSamples);
    },
    [setSelectedSamples],
  );

  const { control } = useForm<FormValues>({
    defaultValues: {
      selectedSamples,
    },
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Text size="sm" className="font-medium">
            Selected samples
          </Text>
          <Badge size="sm" variant="soft">
            {selectedSamples.length} of {samples.length}
          </Badge>
        </div>
        <Controller
          control={control}
          name="selectedSamples"
          render={({ field }) => (
            <GridList
              items={samples}
              selectionMode="multiple"
              selectedKeys={selectedSamples.map((id) => id.toString())}
              onSelectionChange={(keys) => {
                if (typeof keys !== "string" && keys.size === 0) {
                  // disallow deselecting all samples
                  return;
                }
                const keyNums =
                  keys === "all"
                    ? samples.map((s) => s.id)
                    : Array.from(keys).map(Number);
                field.onChange(keyNums);
                setSelectedSamples(keyNums);
              }}
              className="max-h-[400px] overflow-y-auto"
            >
              {(sample) => (
                <GridListItem
                  key={sample.id}
                  id={sample.id.toString()}
                  textValue={sample.tube?.name || "Unknown Tube"}
                >
                  <div className="flex flex-col">
                    <Text size="1" className="font-medium">
                      {sample.tube?.name || "Unknown Tube"}
                    </Text>
                    <Text size="1" className="text-muted-foreground">
                      {sample.collection ? format(sample.collection) : "-"}
                    </Text>
                  </div>
                </GridListItem>
              )}
            </GridList>
          )}
        />
      </div>
    </div>
  );
}

function DownloadButton() {
  const navigate = useNavigate();
  const { accession, labId, selectedSamples } = useRequisition();

  const downloadRequisition = () => {
    const apiUrl = new URL(
      `/api/requisition/${accession.id}`,
      window.location.origin,
    );
    apiUrl.searchParams.set("labId", labId.toString());
    apiUrl.searchParams.set("format", "pdf");

    if (selectedSamples.length > 0) {
      apiUrl.searchParams.set(
        "selectedSamples",
        JSON.stringify(selectedSamples),
      );
    }

    // Create a temporary link and click it to download
    const link = document.createElement("a");
    link.href = apiUrl.toString();
    link.download = `requisition_${accession.id}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Requisition downloaded");
    navigate({ to: "/accessions/$id", params: { id: accession.id } });
  };

  return (
    <div className="flex items-center gap-2">
      <Button size="sm" onPress={downloadRequisition}>
        <DownloadIcon />
        Download PDF
      </Button>
    </div>
  );
}
